import React, { useState, useEffect, useCallback, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Building,
  MapPin,
  Hash,
  CreditCard,
} from "lucide-react";
import axios from "axios";
import favicon from "../assets/favicon_new.png"; 

axios.defaults.baseURL =
  process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:5000"; // Backend URL

// Futuristic Button Component matching Dashboard theme
const Button = ({
  children,
  onClick,
  variant = "primary",
  className = "",
  disabled = false,
  type = "button",
  isDarkMode,
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg flex items-center justify-center transition-all duration-300 text-sm sm:text-base ${
      variant === "primary"
        ? isDarkMode
          ? "bg-blue-500/30 hover:bg-blue-500/40 text-blue-400 hover:text-blue-300 border border-blue-500/30 hover:border-blue-400/50 shadow-md shadow-blue-500/20 hover:shadow-blue-500/30"
          : "bg-blue-500/20 hover:bg-blue-500/30 text-blue-600 hover:text-blue-700 border border-blue-400/30 hover:border-blue-500/50 shadow-sm hover:shadow-md shadow-blue-500/10"
        : variant === "secondary"
        ? isDarkMode
          ? "bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 border border-gray-600/50 hover:border-gray-500/50"
          : "bg-gray-200/50 hover:bg-gray-300/50 text-gray-700 border border-gray-300/50 hover:border-gray-400/50"
        : variant === "danger"
        ? isDarkMode
          ? "bg-gradient-to-r from-rose-500/30 to-red-500/30 hover:from-rose-500/40 hover:to-red-500/40 text-rose-400 border-rose-500/50 hover:text-rose-300 hover:border-rose-400/50 shadow-lg shadow-rose-500/10 hover:shadow-rose-500/20"
          : "bg-gradient-to-r from-red-400/30 to-red-300/30 hover:from-red-400/40 hover:to-red-300/40 text-red-700 border border-red-300 hover:text-red-800 shadow-md hover:shadow-lg"
        : variant === "ghost"
        ? isDarkMode
          ? "bg-transparent hover:bg-gray-700/30 text-gray-400 hover:text-gray-300"
          : "bg-transparent hover:bg-gray-100/50 text-gray-600 hover:text-gray-800"
        : ""
    } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${className}`}
  >
    {children}
  </button>
);

// Fixed Input Component with proper dark/light mode theming
const FormInput = ({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  placeholder = "",
  icon: Icon,
  isDarkMode,
  themeClasses,
}) => (
  <div className="space-y-2">
    <label className={`block text-sm font-medium ${
      isDarkMode ? "text-gray-200" : "text-gray-700"
    }`}>
      {label} {required && <span className="text-rose-400">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <Icon
          className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 z-10 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-500'
          }`}
          style={{ pointerEvents: 'none' }}
        />
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className={`w-full ${
          Icon ? "pl-10" : "pl-4"
        } pr-4 py-2.5 rounded-lg border transition-all duration-300 focus:ring-2 focus:outline-none text-sm sm:text-base ${
          isDarkMode
            ? "bg-gray-700/50 border-gray-600/50 text-gray-100 placeholder-gray-400 focus:ring-blue-500/30 focus:border-blue-400/50 hover:border-gray-500/50"
            : "bg-gray-50/50 border-gray-300/60 text-gray-900 placeholder-gray-500 focus:ring-blue-500/20 focus:border-blue-500/50 hover:border-gray-400/60"
        }`}
      />
    </div>
  </div>
);

// TextArea Component with proper dark/light mode theming
const FormTextArea = ({
  label,
  value,
  onChange,
  required = false,
  placeholder = "",
  rows = 3,
  isDarkMode,
  themeClasses,
}) => (
  <div className="space-y-2">
    <label className={`block text-sm font-medium ${
      isDarkMode ? "text-gray-200" : "text-gray-700"
    }`}>
      {label} {required && <span className="text-rose-400">*</span>}
    </label>
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      rows={rows}
      className={`w-full px-4 py-2.5 rounded-lg border transition-all duration-300 focus:ring-2 focus:outline-none resize-vertical text-sm sm:text-base ${
        isDarkMode
          ? "bg-gray-700/50 border-gray-600/50 text-gray-100 placeholder-gray-400 focus:ring-blue-500/30 focus:border-blue-400/50 hover:border-gray-500/50"
          : "bg-gray-50/50 border-gray-300/60 text-gray-900 placeholder-gray-500 focus:ring-blue-500/20 focus:border-blue-500/50 hover:border-gray-400/60"
      }`}
    />
  </div>
);

// Simplified Company Form with proper theming and minimized dimensions
const CompanyForm = ({
  formData,
  onSubmit,
  onCancel,
  editingCompany,
  isDarkMode,
  themeClasses,
}) => {
  const [localFormData, setLocalFormData] = useState({
    company_name: formData.company_name || "",
    address: formData.address || "",
    gst_number: formData.gst_number || "",
    pan_number: formData.pan_number || "",
    state: formData.state || "",
    country: formData.country || "",
    pincode: formData.pincode || "",
  });

  // Update local state when formData changes (for editing)
  useEffect(() => {
    setLocalFormData({
      company_name: formData.company_name || "",
      address: formData.address || "",
      gst_number: formData.gst_number || "",
      pan_number: formData.pan_number || "",
      state: formData.state || "",
      country: formData.country || "",
      pincode: formData.pincode || "",
    });
  }, [formData]);

  const handleChange = (field, value) => {
    setLocalFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(localFormData);
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
        <div className="grid grid-cols-1 gap-4">
          <FormInput
            label="Company Name"
            value={localFormData.company_name}
            onChange={(e) => handleChange("company_name", e.target.value)}
            required
            placeholder="Enter company name"
            icon={Building}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
          />

          <FormTextArea
            label="Address"
            value={localFormData.address}
            onChange={(e) => handleChange("address", e.target.value)}
            placeholder="Enter company address"
            rows={2}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormInput
              label="GST Number"
              value={localFormData.gst_number}
              onChange={(e) => handleChange("gst_number", e.target.value)}
              placeholder="22AAAAA0000A1Z5"
              icon={Hash}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
            />

            <FormInput
              label="PAN Number"
              value={localFormData.pan_number}
              onChange={(e) => handleChange("pan_number", e.target.value)}
              placeholder="AAAAA0000A"
              icon={CreditCard}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormInput
              label="State"
              value={localFormData.state}
              onChange={(e) => handleChange("state", e.target.value)}
              placeholder="Enter state"
              icon={MapPin}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
            />

            <FormInput
              label="Country"
              value={localFormData.country}
              onChange={(e) => handleChange("country", e.target.value)}
              placeholder="Enter country"
              icon={MapPin}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
            />
          </div>

          <FormInput
            label="Pincode"
            value={localFormData.pincode}
            onChange={(e) => handleChange("pincode", e.target.value)}
            placeholder="400001"
            icon={MapPin}
            isDarkMode={isDarkMode}
            themeClasses={themeClasses}
          />
        </div>

        <div className={`flex flex-col-reverse sm:flex-row justify-end space-y-3 space-y-reverse sm:space-y-0 sm:space-x-3 pt-4 sm:pt-5 mt-4 sm:mt-5 border-t ${
          isDarkMode ? "border-gray-700/50" : "border-gray-200/50"
        }`}>
          <Button 
            variant="secondary" 
            onClick={onCancel} 
            isDarkMode={isDarkMode}
            className="w-full sm:w-auto px-4 sm:px-5 py-2"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="w-full sm:w-auto space-x-2 px-4 sm:px-5 py-2" 
            isDarkMode={isDarkMode}
          >
            <Save className="h-4 w-4" />
            <span>{editingCompany ? "Update" : "Create"} Company</span>
          </Button>
        </div>
      </form>
    </div>
  );
};

const CompanyPage = memo(
  ({
    companies,
    setCompanies,
    selectedCompany,
    setSelectedCompany,
    isDarkMode,
    themeClasses,
  }) => {
    const [error, setError] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [editingCompany, setEditingCompany] = useState(null);
    const [formData, setFormData] = useState({
      company_name: "",
      address: "",
      gst_number: "",
      state: "",
      country: "",
      pincode: "",
      pan_number: "",
    });

    // Set loading based on whether companies data is available
    const isLoading = companies === undefined || companies === null;

    // Clear error when companies data is loaded successfully
    useEffect(() => {
      if (companies && companies.length >= 0) {
        setError("");
      }
    }, [companies]);

    const resetForm = useCallback(() => {
      setFormData({
        company_name: "",
        address: "",
        gst_number: "",
        state: "",
        country: "",
        pincode: "",
        pan_number: "",
      });
      setEditingCompany(null);
      setShowForm(false);
    }, []);

    const refreshCompanies = useCallback(async () => {
      try {
        const token = localStorage.getItem("authToken");
        const response = await axios.get("/api/companies", {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        const data = response.data;
        const companiesData = Array.isArray(data) ? data : data.companies || [];
        setCompanies(companiesData);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    }, [setCompanies]);

    const handleFormSubmit = useCallback(
      async (formDataToSubmit) => {
        try {
          const token = localStorage.getItem("authToken");
          const url = editingCompany
            ? `/api/companies/${editingCompany.id}`
            : "/api/companies";
          const method = editingCompany ? "put" : "post";

          await axios({
            url,
            method,
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            data: formDataToSubmit,
          });

          await refreshCompanies();
          resetForm();
          setError("");
        } catch (err) {
          setError(err.response?.data?.error || err.message);
        }
      },
      [editingCompany, refreshCompanies, resetForm]
    );

    const handleDelete = async (companyId) => {
      if (!window.confirm("Are you sure you want to delete this company?")) {
        return;
      }

      try {
        const token = localStorage.getItem("authToken");
        await axios.delete(`/api/companies/${companyId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        await refreshCompanies();
        setError("");
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      }
    };

    const handleEdit = (company) => {
      setEditingCompany(company);
      setFormData({
        company_name: company.company_name || "",
        address: company.address || "",
        gst_number: company.gst_number || "",
        state: company.state || "",
        country: company.country || "",
        pincode: company.pincode || "",
        pan_number: company.pan_number || "",
      });
      setShowForm(true);
    };

    // Futuristic Card Component matching Dashboard theme
    const Card = ({ children, className = "" }) => (
      <div
        className={`${
          themeClasses.card
        } rounded-xl transition-all duration-300 hover:shadow-lg ${
          isDarkMode ? "hover:shadow-blue-500/10" : "hover:shadow-blue-500/5"
        } ${className}`}
      >
        {children}
      </div>
    );

    // Modal Card Component with solid background for better visibility
    const ModalCard = ({ children, className = "" }) => (
      <div
        className={`rounded-xl transition-all duration-300 shadow-2xl ${
          isDarkMode
            ? "bg-gray-900 border border-gray-700/50"
            : "bg-white border border-gray-200/50"
        } ${className}`}
      >
        {children}
      </div>
    );

    if (isLoading) {
      return (
        <div className="space-y-6 p-4 sm:p-6">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <div className="relative w-6 h-6 sm:w-7 sm:h-7 mb-5">
                <motion.div
                  className="w-full h-full"
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 2,
                    ease: "linear",
                    repeat: Infinity,
                  }}
                >
                  <img
                    src={favicon}
                    alt="Loading"
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      console.log("Image failed to load:", e.target.src);
                      e.target.style.display = "none";
                    }}
                  />
                </motion.div>
              </div>
              <span className={`text-base sm:text-lg ${themeClasses.text}`}>
                Loading companies...
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <div
              className={`p-2 rounded-lg ${
                isDarkMode ? "bg-blue-500/20" : "bg-blue-100"
              }`}
            >
              <Building
                className={`h-5 w-5 sm:h-6 sm:w-6 ${
                  isDarkMode ? "text-blue-400" : "text-blue-600"
                }`}
              />
            </div>
            <div>
              <h2
                className={`text-xl sm:text-2xl font-bold ${themeClasses.text} font-heading`}
              >
                Company Management
              </h2>
              <p className={`text-sm sm:text-base ${themeClasses.subtext}`}>
                Manage your business information and company details
              </p>
            </div>
          </div>
          <Button
            onClick={() => setShowForm(true)}
            className="space-x-2 w-full sm:w-auto"
            isDarkMode={isDarkMode}
          >
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Add Company</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className={`p-3 sm:p-4 rounded-lg border text-sm sm:text-base ${
                isDarkMode
                  ? "bg-rose-500/10 border-rose-500/30 text-rose-400"
                  : "bg-red-50 border-red-200 text-red-700"
              }`}
            >
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Company Form Modal */}
        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 p-4"
              onClick={(e) => {
                if (e.target === e.currentTarget) resetForm();
              }}
            >
              <div className="flex items-start sm:items-center justify-center min-h-full">
                <motion.div
                  initial={{ scale: 0.95, opacity: 0, y: 20 }}
                  animate={{ scale: 1, opacity: 1, y: 0 }}
                  exit={{ scale: 0.95, opacity: 0, y: 20 }}
                  className="w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] relative mt-4 sm:mt-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ModalCard className="p-0 overflow-hidden h-full flex flex-col">
                    {/* Header */}
                    <div className={`p-4 sm:p-6 border-b flex items-center justify-between flex-shrink-0 ${
                      isDarkMode ? "border-gray-700" : "border-gray-200"
                    }`}>
                      <h3
                        className={`text-lg sm:text-xl font-semibold ${themeClasses.text}`}
                      >
                        {editingCompany ? "Edit Company" : "Add New Company"}
                      </h3>
                      <Button
                        variant="ghost"
                        onClick={resetForm}
                        isDarkMode={isDarkMode}
                        className="p-1 sm:p-2"
                      >
                        <X className="h-4 w-4 sm:h-5 sm:w-5" />
                      </Button>
                    </div>
                    
                    {/* Scrollable Content */}
                    <div className="overflow-y-auto flex-1 p-4 sm:p-6">
                      <CompanyForm
                        formData={formData}
                        onSubmit={handleFormSubmit}
                        onCancel={resetForm}
                        editingCompany={editingCompany}
                        isDarkMode={isDarkMode}
                        themeClasses={themeClasses}
                      />
                    </div>
                  </ModalCard>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Companies List */}
        <div className="space-y-4">
          {companies.length === 0 ? (
            <Card className="p-6 sm:p-8 text-center">
              <div className="flex flex-col items-center space-y-4">
                <div
                  className={`p-3 sm:p-4 rounded-full ${
                    isDarkMode ? "bg-gray-700/50" : "bg-gray-100"
                  }`}
                >
                  <Building className={`h-10 w-10 sm:h-12 sm:w-12 ${themeClasses.subtext}`} />
                </div>
                <div>
                  <h3
                    className={`text-lg sm:text-xl font-semibold mb-2 ${themeClasses.text}`}
                  >
                    No Companies Found
                  </h3>
                  <p className={`${themeClasses.subtext} mb-4 max-w-md text-sm sm:text-base px-4 sm:px-0`}>
                    Start by adding your first company to manage your business
                    information and streamline your workflow.
                  </p>
                </div>
                <Button
                  onClick={() => setShowForm(true)}
                  className="space-x-2 w-full sm:w-auto"
                  isDarkMode={isDarkMode}
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Your First Company</span>
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid gap-4">
              {companies.map((company, index) => (
                <motion.div
                  key={company.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className="p-4 sm:p-6">
                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start space-y-4 lg:space-y-0">
                      <div className="flex-1">
                        <div className="flex items-start space-x-3 mb-4">
                          <div
                            className={`p-2 rounded-lg flex-shrink-0 ${
                              isDarkMode ? "bg-blue-500/20" : "bg-blue-100"
                            }`}
                          >
                            <Building
                              className={`h-4 w-4 sm:h-5 sm:w-5 ${
                                isDarkMode ? "text-blue-400" : "text-blue-600"
                              }`}
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3
                              className={`text-lg sm:text-xl font-semibold ${themeClasses.text} break-words`}
                            >
                              {company.company_name}
                            </h3>
                            <p className={`text-xs sm:text-sm ${themeClasses.subtext}`}>
                              Company ID: {company.id}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                          {company.address && (
                            <div
                              className={`p-3 rounded-lg ${
                                isDarkMode ? "bg-gray-800/30" : "bg-gray-50/50"
                              }`}
                            >
                              <div className="flex items-center space-x-2 mb-1">
                                <MapPin
                                  className={`h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 ${themeClasses.subtext}`}
                                />
                                <span
                                  className={`text-xs sm:text-sm font-medium ${themeClasses.subtext}`}
                                >
                                  Address
                                </span>
                              </div>
                              <p className={`text-xs sm:text-sm ${themeClasses.text} break-words`}>
                                {company.address}
                              </p>
                            </div>
                          )}

                          {company.gst_number && (
                            <div
                              className={`p-3 rounded-lg ${
                                isDarkMode ? "bg-gray-800/30" : "bg-gray-50/50"
                              }`}
                            >
                              <div className="flex items-center space-x-2 mb-1">
                                <Hash
                                  className={`h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 ${themeClasses.subtext}`}
                                />
                                <span
                                  className={`text-xs sm:text-sm font-medium ${themeClasses.subtext}`}
                                >
                                  GST Number
                                </span>
                              </div>
                              <p
                                className={`text-xs sm:text-sm font-mono ${themeClasses.text} break-all`}
                              >
                                {company.gst_number}
                              </p>
                            </div>
                          )}

                          {company.pan_number && (
                            <div
                              className={`p-3 rounded-lg ${
                                isDarkMode ? "bg-gray-800/30" : "bg-gray-50/50"
                              }`}
                            >
                              <div className="flex items-center space-x-2 mb-1">
                                <CreditCard
                                  className={`h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 ${themeClasses.subtext}`}
                                />
                                <span
                                  className={`text-xs sm:text-sm font-medium ${themeClasses.subtext}`}
                                >
                                  PAN Number
                                </span>
                              </div>
                              <p
                                className={`text-xs sm:text-sm font-mono ${themeClasses.text} break-all`}
                              >
                                {company.pan_number}
                              </p>
                            </div>
                          )}

                          {(company.state ||
                            company.country ||
                            company.pincode) && (
                            <div
                              className={`p-3 rounded-lg ${
                                isDarkMode ? "bg-gray-800/30" : "bg-gray-50/50"
                              } ${company.address && company.gst_number && company.pan_number ? 'sm:col-span-2 xl:col-span-1' : ''}`}
                            >
                              <div className="flex items-center space-x-2 mb-1">
                                <MapPin
                                  className={`h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 ${themeClasses.subtext}`}
                                />
                                <span
                                  className={`text-xs sm:text-sm font-medium ${themeClasses.subtext}`}
                                >
                                  Location
                                </span>
                              </div>
                              <p className={`text-xs sm:text-sm ${themeClasses.text} break-words`}>
                                {[
                                  company.state,
                                  company.country,
                                  company.pincode,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex lg:flex-col space-x-2 lg:space-x-0 lg:space-y-2 lg:ml-4 justify-end lg:justify-start">
                        <Button
                          variant="ghost"
                          onClick={() => handleEdit(company)}
                          className={`p-2 ${
                            isDarkMode
                              ? "text-blue-400 hover:text-blue-300"
                              : "text-blue-600 hover:text-blue-700"
                          }`}
                          isDarkMode={isDarkMode}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => handleDelete(company.id)}
                          className={`p-2 ${
                            isDarkMode
                              ? "text-rose-400 hover:text-rose-300"
                              : "text-red-600 hover:text-red-700"
                          }`}
                          isDarkMode={isDarkMode}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default CompanyPage;